import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { catchError, Observable, throwError } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { environment } from "src/environment/environment";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly HEALTH_URL = `${environment.apiUrl}/health`;

  // ✅ Prevents multiple redirects from parallel API failures
  private isRedirectingToServerDown = false;

  constructor(
    private toastr: ToastrService,
    private router: Router
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isBackgroundRequest = req.headers.get("X-Background-Request") === "true";
    const request = isBackgroundRequest
      ? req.clone({ headers: req.headers.delete("X-Background-Request") })
      : req;

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isBackgroundRequest) {
          return throwError(() => error);
        }

        // 🔐 AUTH ERRORS → NEVER show toaster here
        const isLoggedIn = !!localStorage.getItem("accessToken");

        // 🔐 Suppress token-expired error ONLY for logged-in users
        if (error.error?.code === "AUTH_008" && isLoggedIn) {
          return throwError(() => error);
        }

        // ✅ Enterprise-grade "Server Down" statuses
        const serverDownStatuses = [0, 502, 503, 504];

        // ✅ SERVER / NETWORK FAILURE
        if (serverDownStatuses.includes(error.status)) {
          // 🚫 NEVER handle health API here (prevents infinite loop)
          if (request.url === this.HEALTH_URL) {
            return throwError(() => error);
          }

          // ✅ Redirect ONLY ONCE
          if (
            !this.isRedirectingToServerDown &&
            !this.router.url.includes("server-down")
          ) {
            this.isRedirectingToServerDown = true;

            this.toastr.error(
              "Server is currently unavailable. Please try again later.",
              "Server Down"
            );

            this.router.navigate(["/server-down"]).then(() => {
              this.isRedirectingToServerDown = false;
            });
          }

          return throwError(() => error);
        }

        // ❗ REAL BUSINESS ERRORS ONLY
        if (error.status >= 400) {
          const message =
            error.error?.message ||
            "Something went wrong while processing your request.";

          this.toastr.error(message, "Request Failed");
        }

        return throwError(() => error);
      })
    );
  }
}
